import { useEffect, useState } from "react";
import api from "../lib/api";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, BarChart, Bar, Legend, Cell } from "recharts";
import { formatEURDecimal } from "../lib/format";
import { TrendingUp, TrendingDown, Timer, Wallet, Users, Building2, Award, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const currency = (v) => formatEURDecimal(v);
const compact = (v) => new Intl.NumberFormat("es-ES", { notation: "compact", maximumFractionDigits: 1 }).format(v);

export default function KPIDashboard() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/kpis/metrics?year=${year}`)
      .then(r => setData(r.data))
      .catch(err => toast.error(err?.response?.data?.detail || "Error cargando KPIs"))
      .finally(() => setLoading(false));
  }, [year]);

  if (loading) return <div className="p-10 text-center text-sm text-[var(--ink-muted)]">Cargando KPIs…</div>;
  if (!data) return null;

  const { cashflow_evolution, runway, project_margins, monthly_income_expense, yoy_comparison, top_proveedores, total_gasto_year } = data;

  return (
    <div className="p-6 max-w-[1400px] mx-auto space-y-6" data-testid="kpi-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="font-mono-num text-[10px] uppercase tracking-widest text-[var(--ink-muted)]">Dashboard</p>
          <h2 className="font-display text-2xl font-black">Métricas · {year}</h2>
        </div>
        <select value={year} onChange={e => setYear(parseInt(e.target.value))} className="border border-[var(--border)] rounded px-3 py-1.5 text-sm bg-white">
          {[year-2, year-1, year, year+1].map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      {/* Top KPI cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
        <KPICard icon={<Wallet size={16}/>} label="Saldo Actual" value={currency(cashflow_evolution.saldo_actual)} color="ink" testid="kpi-saldo" />
        <KPICard icon={<Award size={16}/>} label={`Facturado ${year}`} value={currency(yoy_comparison.current.facturado_neto)} sub={`${yoy_comparison.current.n_facturas} facturas · cobrado ${currency(yoy_comparison.current.cobrado)}`} color="emerald" testid="kpi-facturado"/>
        <KPICard icon={<TrendingUp size={16}/>} label="Cash Flow Fin Año" value={currency(cashflow_evolution.cashflow_fin_año)} color={cashflow_evolution.cashflow_fin_año >= cashflow_evolution.minimo_seguro ? "emerald" : "rose"} testid="kpi-cashflow-fin" />
        <KPICard icon={<Timer size={16}/>} label="Runway (meses)" value={`${runway.meses || "—"}`} sub={`${currency(runway.gastos_fijos_mensuales)}/mes`} color={runway.meses > 6 ? "emerald" : runway.meses > 3 ? "amber" : "rose"} testid="kpi-runway" />
        <KPICard icon={<AlertTriangle size={16}/>} label="Mínimo seguro" value={currency(cashflow_evolution.minimo_seguro)} sub="3× gastos fijos mensuales" color="ink" testid="kpi-minimo" />
      </div>

      {/* Cashflow Evolution */}
      <Section title="Evolución del Cash Flow" subtitle="Saldo proyectado quincena a quincena. Línea roja = mínimo seguro.">
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={cashflow_evolution.series} margin={{ top: 5, right: 20, bottom: 5, left: 60 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eee"/>
            <XAxis dataKey="label" fontSize={10} tick={{ fill: "#6b7280" }}/>
            <YAxis fontSize={10} tick={{ fill: "#6b7280" }} tickFormatter={compact}/>
            <Tooltip formatter={(v) => currency(v)} contentStyle={{ fontSize: 12 }}/>
            <ReferenceLine y={cashflow_evolution.minimo_seguro} stroke="#dc2626" strokeDasharray="4 4" label={{ value: "Mínimo seguro", position: "insideTopRight", fontSize: 10, fill: "#dc2626" }}/>
            <ReferenceLine y={0} stroke="#9ca3af" strokeWidth={1}/>
            <Line type="monotone" dataKey="value" stroke="#111" strokeWidth={2} dot={{ r: 3, fill: "#111" }}/>
          </LineChart>
        </ResponsiveContainer>
      </Section>

      {/* Ingresos vs Gastos mensual */}
      <Section title="Ingresos vs Gastos por mes" subtitle="Solo cobrados/pagados (real).">
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={monthly_income_expense} margin={{ top: 5, right: 20, bottom: 5, left: 60 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eee"/>
            <XAxis dataKey="label" fontSize={10} tick={{ fill: "#6b7280" }}/>
            <YAxis fontSize={10} tick={{ fill: "#6b7280" }} tickFormatter={compact}/>
            <Tooltip formatter={(v) => currency(v)} contentStyle={{ fontSize: 12 }}/>
            <Legend wrapperStyle={{ fontSize: 11 }}/>
            <Bar dataKey="ingresos" fill="#10b981" name="Ingresos"/>
            <Bar dataKey="gastos" fill="#ef4444" name="Gastos"/>
          </BarChart>
        </ResponsiveContainer>
      </Section>

      {/* Márgenes por proyecto */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Section title="🏆 Top proyectos por margen" subtitle="Los que más te dejan (histórico real)">
          <div className="divide-y divide-[var(--border)]">
            {project_margins.top.slice(0, 8).map((p) => (
              <div key={p.code} className="py-2 flex items-center gap-3 text-sm">
                <span className="font-mono-num text-xs text-[var(--ink-muted)] w-12">{p.code}</span>
                <span className="flex-1 truncate">{p.name}</span>
                <span className="font-mono-num text-emerald-700 font-semibold">{currency(p.margen)}</span>
                <span className="text-[10px] text-[var(--ink-muted)] font-mono-num w-10 text-right">{p.pct?.toFixed(0) || "—"}%</span>
              </div>
            ))}
            {project_margins.top.length === 0 && <div className="text-sm text-[var(--ink-muted)] py-2">Sin datos aún.</div>}
          </div>
        </Section>
        <Section title="⚠️ Peores márgenes" subtitle="Los que te dan pérdida o cero rentabilidad">
          <div className="divide-y divide-[var(--border)]">
            {project_margins.worst.slice(0, 8).map((p) => (
              <div key={p.code} className="py-2 flex items-center gap-3 text-sm">
                <span className="font-mono-num text-xs text-[var(--ink-muted)] w-12">{p.code}</span>
                <span className="flex-1 truncate">{p.name}</span>
                <span className="font-mono-num text-rose-700 font-semibold">{currency(p.margen)}</span>
                <span className="text-[10px] text-[var(--ink-muted)] font-mono-num w-10 text-right">{p.pct?.toFixed(0) || "—"}%</span>
              </div>
            ))}
            {project_margins.worst.length === 0 && <div className="text-sm text-[var(--ink-muted)] py-2">Sin datos aún.</div>}
          </div>
        </Section>
      </div>

      {/* Top proveedores */}
      <Section title="💰 Gasto por proveedor (top 20)" subtitle={`Concentración de tu gasto. Total gasto YTD: ${currency(total_gasto_year)}`}>
        <ResponsiveContainer width="100%" height={Math.max(300, top_proveedores.length * 22)}>
          <BarChart data={top_proveedores} layout="vertical" margin={{ top: 5, right: 60, bottom: 5, left: 140 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eee"/>
            <XAxis type="number" fontSize={10} tick={{ fill: "#6b7280" }} tickFormatter={compact}/>
            <YAxis type="category" dataKey="proveedor" fontSize={10} tick={{ fill: "#6b7280" }} width={140} tickFormatter={(v) => v?.length > 22 ? v.slice(0, 22) + "…" : v}/>
            <Tooltip formatter={(v) => currency(v)} contentStyle={{ fontSize: 12 }}/>
            <Bar dataKey="total" fill="#111">
              {top_proveedores.map((p, i) => (
                <Cell key={i} fill={p.pct > 10 ? "#dc2626" : p.pct > 5 ? "#f59e0b" : "#6b7280"} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        <p className="text-[10px] text-[var(--ink-muted)] mt-2">🔴 &gt;10% del gasto anual · 🟡 5-10% · ⚫ &lt;5%</p>
      </Section>

      {/* Comparativa YoY */}
      <Section title={`📊 Comparativa ${yoy_comparison.previous_year} vs ${yoy_comparison.current_year}`} subtitle="Facturado emitido, cobrado real y gastos. Si el año anterior está a 0 no había datos en el sistema.">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <YoyBox label="Facturado (emitido)" prev={yoy_comparison.previous.facturado_neto} curr={yoy_comparison.current.facturado_neto} growth={yoy_comparison.growth_facturado_pct} color="emerald"/>
          <YoyBox label="Cobrado real" prev={yoy_comparison.previous.cobrado} curr={yoy_comparison.current.cobrado} growth={yoy_comparison.growth_cobrado_pct} color="emerald"/>
          <YoyBox label="Gastos" prev={yoy_comparison.previous.gastos} curr={yoy_comparison.current.gastos} color="rose" inverted/>
          <YoyBox label="Margen (cobrado − gastos)" prev={yoy_comparison.previous.margen} curr={yoy_comparison.current.margen} growth={yoy_comparison.growth_margen_pct} color="ink"/>
        </div>
        {yoy_comparison.current.pendiente_cobrar > 0 && (
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-900">
            💡 Pendiente de cobrar en {yoy_comparison.current_year}: <strong>{currency(yoy_comparison.current.pendiente_cobrar)}</strong>
            {yoy_comparison.current.abonado > 0 && (<> · Abonado (notas de crédito): <strong>{currency(yoy_comparison.current.abonado)}</strong></>)}
          </div>
        )}
      </Section>
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <div className="bg-white border border-[var(--border)] rounded-lg p-5">
      <h3 className="font-display text-lg font-black leading-tight">{title}</h3>
      {subtitle && <p className="text-xs text-[var(--ink-muted)] mt-0.5 mb-4">{subtitle}</p>}
      {children}
    </div>
  );
}

function KPICard({ icon, label, value, sub, color, testid }) {
  const colorMap = { emerald: "text-emerald-700", rose: "text-rose-700", amber: "text-amber-600", ink: "text-[var(--ink)]" };
  return (
    <div className="bg-white border border-[var(--border)] rounded-lg p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-[var(--ink-muted)] font-mono-num">
        {icon} {label}
      </div>
      <div className={`mt-1 font-mono-num text-2xl font-bold ${colorMap[color] || ""}`}>{value}</div>
      {sub && <div className="text-[10px] text-[var(--ink-muted)] mt-0.5">{sub}</div>}
    </div>
  );
}

function YoyBox({ label, prev, curr, growth, color, inverted }) {
  const positive = inverted ? curr < prev : curr > prev;
  const growthColor = growth === null ? "text-[var(--ink-muted)]" : positive ? "text-emerald-700" : "text-rose-700";
  return (
    <div className="border border-[var(--border)] rounded-lg p-4">
      <div className="text-xs uppercase tracking-wider text-[var(--ink-muted)] font-mono-num mb-2">{label}</div>
      <div className="flex items-baseline justify-between gap-3">
        <div>
          <div className="text-[10px] text-[var(--ink-muted)]">Anterior</div>
          <div className="font-mono-num text-sm">{currency(prev)}</div>
        </div>
        <div className="text-right">
          <div className="text-[10px] text-[var(--ink-muted)]">Actual</div>
          <div className={`font-mono-num text-lg font-semibold ${color === "emerald" ? "text-emerald-700" : color === "rose" ? "text-rose-700" : ""}`}>{currency(curr)}</div>
        </div>
      </div>
      {growth !== null && growth !== undefined && (
        <div className={`text-xs mt-2 flex items-center gap-1 ${growthColor}`}>
          {positive ? <TrendingUp size={12}/> : <TrendingDown size={12}/>}
          {growth > 0 ? "+" : ""}{growth}%
        </div>
      )}
    </div>
  );
}
